import React from "react";
import { Box, Stack, Typography } from "@mui/joy";
import { useTranslation } from "react-i18next";
import WelcomeHeroSurface, { HERO_IMAGE_HIDDEN_QUERY } from "./WelcomeHeroSurface";
import { useIndustryType, IndustryType } from "../../hooks/useIndustryType";
import { isContaminationDeclarationsEnabled } from "../../utils/contaminationDeclarationsEnabled";

const OTTO_BULLET_KEYS: readonly string[] = [
  "Centralized overview of your installed equipment",
  "Improved visibility of equipment and asset information",
  "Clearer information on alerts, service reminders, and events",
  "Centralized access through the Busch Group Customer Portal",
  "Easier access to relevant equipment and service information",
];

// Feature-flagged (build-time constant, see src/utils/contaminationDeclarationsEnabled.ts).
const DOC_BULLET_KEY = "Assisted contamination declaration preparation using chamber data";

const ARGOS_BULLET_KEYS: readonly string[] = [
  "Complete equipment lifecycle and service history tracking",
  "Recording of installations, pump exchanges, and service interventions",
  "Customizable reporting and data export capabilities",
  "Improved visibility of assets across sites, fabs, tools, and chambers",
  ...(isContaminationDeclarationsEnabled() ? [DOC_BULLET_KEY] : []),
  "Enhanced data foundation for service optimization and continuous improvement",
];

const BODY_COLOR = "rgba(255,255,255,0.78)";

/**
 * Permanent welcome/what's-new intro at the top of the About page. Shares the
 * dark hero visual with the dashboard welcome banner and carries the full
 * per-brand welcome text; the banner links here via "Explore what's new".
 */
const AboutWelcomeSection: React.FC = () => {
  const { t } = useTranslation();
  const isArgos = useIndustryType() === IndustryType.SEMICONDUCTOR;

  const introParagraphKeys = isArgos
    ? [
        "ARGOS IBM is your centralized platform for managing installed equipment...",
        "The platform also simplifies reporting...",
        "To help you get started, here are the most important capabilities available in the new experience.",
      ]
    : [
        "OTTO is now part of the Busch Group Customer Portal, giving you centralized access to your installed equipment and digital services.",
        "To help you get started, here is an overview of the most important improvements in the new platform.",
      ];

  const bulletKeys = isArgos ? ARGOS_BULLET_KEYS : OTTO_BULLET_KEYS;

  return (
    <WelcomeHeroSurface style={{ minHeight: 260, height: "auto" }} imageSide="right">
      <Stack
        spacing={1.5}
        sx={{
          padding: "28px clamp(48px, calc(42% + 24px), 664px) 28px 32px",
          [HERO_IMAGE_HIDDEN_QUERY]: { padding: "28px 32px" },
        }}
      >
        <Typography level="titleMedium" sx={{ color: "#fff" }}>
          {isArgos ? t("Welcome to the new IBM System powered by ARGOS") : t("Welcome to the new OTTO platform")}
        </Typography>
        {introParagraphKeys.map(key => (
          <Typography key={key} level="bodySmall" sx={{ color: BODY_COLOR }}>
            {t(key)}
          </Typography>
        ))}
        <Typography level="titleSmall" sx={{ color: "#fff", mt: 1 }}>
          {t("What's new")}
        </Typography>
        {isArgos && (
          <Typography
            level="body-xs"
            fontWeight="lg"
            sx={{ color: BODY_COLOR, textTransform: "uppercase", letterSpacing: "0.6px" }}
          >
            {t("Centralized Installed Base Management")}
          </Typography>
        )}
        <Box component="ul" sx={{ m: 0, pl: 2.5, listStyleType: "disc" }}>
          {bulletKeys.map(key => (
            <Typography
              component="li"
              level="bodySmall"
              sx={{ color: BODY_COLOR, display: "list-item", mb: "2px", "&::marker": { color: "rgba(255,255,255,0.4)" } }}
              key={key}
            >
              {t(key)}
            </Typography>
          ))}
        </Box>
      </Stack>
    </WelcomeHeroSurface>
  );
};

export default AboutWelcomeSection;
